"use client"
/* */
import { jsx, jsxs } from 'react/jsx-runtime';
import 'react';
import styles from './Button.module.scss.mjs';
import * as Aria from 'react-aria-components';
import clsx from 'clsx';
import { ClearPropsContext } from '../ClearPropsContext/ClearPropsContext.mjs';
import '../../lib/propsContext/propsContext.mjs';
import { PropsContextProvider } from '../../lib/propsContext/PropsContextProvider.mjs';
import '@tabler/icons-react';
import '../Icon/Icon.mjs';
import '../../lib/viewComponentContext/viewComponentContext.mjs';
import { IconFailed } from '../Icon/components/icons/IconFailed.mjs';
import { IconSucceeded } from '../Icon/components/icons/IconSucceeded.mjs';
import { Wrap } from '../Wrap/Wrap.mjs';
import { Text } from '../Text/Text.mjs';
import { flowComponent } from '../../lib/componentFactory/flowComponent.mjs';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner.mjs';
import { useAriaAnnounceActionState } from '../Action/lib/ariaLive.mjs';

const disablePendingProps = (props) => {
  if (props.isPending || props.isSucceeded || props.isFailed || props["aria-disabled"]) {
    props = { ...props };
    props.onPress = void 0;
    props.onPressStart = void 0;
    props.onPressEnd = void 0;
    props.onPressChange = void 0;
    props.onPressUp = void 0;
    props.onKeyDown = void 0;
    props.onKeyUp = void 0;
  }
  return props;
};
const Button = flowComponent("Button", (props) => {
  props = disablePendingProps(props);
  const {
    color = "primary",
    variant = "solid",
    children,
    className,
    size = "m",
    isPending,
    isSucceeded,
    isFailed,
    "aria-disabled": ariaDisabled,
    ref,
    slot: ignoredSlotProp,
    ariaSlot: slot,
    unstyled,
    ...restProps
  } = props;
  const rootClassName = unstyled ? className : clsx(
    styles.button,
    isPending && styles.isPending,
    isSucceeded && styles.isSucceeded,
    isFailed && styles.isFailed,
    styles[`size-${size}`],
    styles[color],
    styles[variant],
    className,
    /**
     * Workaround warning: The Aria.Button does not support "aria-disabled"
     * by now, so this Button will be visually disabled via CSS.
     */
    ariaDisabled && styles.ariaDisabled
  );
  useAriaAnnounceActionState(
    isPending ? "isPending" : isSucceeded ? "isSucceeded" : isFailed ? "isFailed" : "isIdle"
  );
  const propsContext = {
    Icon: {
      className: styles.icon,
      "aria-hidden": true,
      size
    },
    Text: {
      className: styles.text
    },
    Avatar: {
      className: styles.avatar
    },
    CounterBadge: {
      className: styles.counterBadge
    },
    Image: {
      className: styles.image
    }
  };
  const StateIconComponent = isSucceeded ? IconSucceeded : isFailed ? IconFailed : isPending ? LoadingSpinner : void 0;
  const stateIcon = StateIconComponent && /* @__PURE__ */ jsx(
    StateIconComponent,
    {
      size,
      className: styles.stateIcon,
      status: isFailed ? "danger" : isSucceeded ? "success" : void 0
    }
  );
  const isStringContent = typeof children === "string";
  return /* @__PURE__ */ jsx(ClearPropsContext, { children: /* @__PURE__ */ jsxs(
    Aria.Button,
    {
      className: rootClassName,
      ref,
      slot,
      ...restProps,
      children: [
        /* @__PURE__ */ jsx(PropsContextProvider, { props: propsContext, children: /* @__PURE__ */ jsx(Wrap, { if: !unstyled, children: /* @__PURE__ */ jsx("span", { className: styles.content, children: /* @__PURE__ */ jsx(Wrap, { if: isStringContent, children: /* @__PURE__ */ jsx(Text, { className: styles.text, children }) }) }) }) }),
        stateIcon
      ]
    }
  ) });
});

export { Button, Button as default };
//# sourceMappingURL=Button.mjs.map
